/**
 * AI Analysis Engine for Portfolio Analysis
 * Generates intelligent commentary and recommendations based on
 * backtest and Monte Carlo results, with enhanced scoring and insights.
 */

export const analyzeBacktestResults = (backtestResults, allocations, options = {}) => {
  if (!backtestResults || !backtestResults.metrics) return null;

  const { metrics, totalReturn, finalValue, initialInvestment } = backtestResults;
  const { assumedInflationRate = 2.5, benchmarkReturn = 8 } = options;

  const insights = [];
  const recommendations = [];
  let overallRating = 'Good';
  let riskLevel = 'Medium';

  /** ---------- Annualized Return ---------- **/
  if (metrics.annualizedReturn > 10) {
    insights.push({
      type: 'positive',
      title: 'Strong Historical Performance',
      description: `Your portfolio achieved an annualized return of ${metrics.annualizedReturn.toFixed(2)}%, above the market average of ${benchmarkReturn}%.`
    });
    overallRating = 'Excellent';
  } else if (metrics.annualizedReturn > 7) {
    insights.push({
      type: 'positive',
      title: 'Solid Historical Performance',
      description: `Your annualized return of ${metrics.annualizedReturn.toFixed(2)}% is in line with historical market averages.`
    });
  } else if (metrics.annualizedReturn > 0) {
    insights.push({
      type: 'neutral',
      title: 'Moderate Historical Performance',
      description: `Your portfolio returned ${metrics.annualizedReturn.toFixed(2)}%, below market averages.`
    });
    overallRating = 'Fair';
  } else {
    insights.push({
      type: 'negative',
      title: 'Negative Historical Performance',
      description: `Your portfolio’s annualized return of ${metrics.annualizedReturn.toFixed(2)}% indicates underperformance.`
    });
    overallRating = 'Poor';
  }

  /** ---------- Inflation-Adjusted Return ---------- **/
  const realReturn = metrics.annualizedReturn - assumedInflationRate;
  insights.push({
    type: realReturn > 0 ? 'positive' : 'warning',
    title: 'Inflation-Adjusted Return',
    description: `After accounting for ${assumedInflationRate.toFixed(1)}% inflation, your real annualized return is ${realReturn.toFixed(2)}%.`
  });

  /** ---------- Volatility ---------- **/
  if (metrics.volatility > 20) {
    insights.push({
      type: 'warning',
      title: 'High Volatility Detected',
      description: `Volatility of ${metrics.volatility.toFixed(2)}% indicates significant price swings.`
    });
    riskLevel = 'High';
    recommendations.push({
      priority: 'high',
      title: 'Reduce Portfolio Volatility',
      description: 'Increase allocation to bonds or cash for stability.',
      impact: 'Could lower volatility by 5–10%.'
    });
  } else if (metrics.volatility > 12) {
    insights.push({
      type: 'neutral',
      title: 'Moderate Volatility',
      description: `Volatility of ${metrics.volatility.toFixed(2)}% is typical for a balanced portfolio.`
    });
  } else {
    insights.push({
      type: 'positive',
      title: 'Low Volatility',
      description: `Volatility of ${metrics.volatility.toFixed(2)}% shows consistent performance.`
    });
    riskLevel = 'Low';
  }

  /** ---------- Max Drawdown ---------- **/
  if (metrics.maxDrawdown > 30) {
    insights.push({
      type: 'warning',
      title: 'Significant Drawdown Risk',
      description: `Maximum drawdown of ${metrics.maxDrawdown.toFixed(2)}% shows vulnerability in downturns.`
    });
    recommendations.push({
      priority: 'high',
      title: 'Improve Downside Protection',
      description: 'Add defensive assets like bonds or cash.',
      impact: 'Could reduce drawdown by 10–15%.'
    });
  } else if (metrics.maxDrawdown > 15) {
    insights.push({
      type: 'neutral',
      title: 'Moderate Drawdown',
      description: `Maximum drawdown of ${metrics.maxDrawdown.toFixed(2)}% is within normal equity ranges.`
    });
  } else {
    insights.push({
      type: 'positive',
      title: 'Strong Downside Protection',
      description: `Maximum drawdown of only ${metrics.maxDrawdown.toFixed(2)}% shows strong resilience.`
    });
  }

  /** ---------- Sharpe Ratio ---------- **/
  const sharpe = metrics.sharpeRatio;
  if (sharpe > 2.0) {
    insights.push({ type: 'positive', title: 'Exceptional Risk-Adjusted Returns', description: `Sharpe ratio ${sharpe.toFixed(2)} reflects excellent efficiency.` });
  } else if (sharpe > 1.5) {
    insights.push({ type: 'positive', title: 'Strong Risk-Adjusted Returns', description: `Sharpe ratio ${sharpe.toFixed(2)} indicates robust risk-reward balance.` });
  } else if (sharpe > 1.0) {
    insights.push({ type: 'neutral', title: 'Decent Risk-Adjusted Returns', description: `Sharpe ratio ${sharpe.toFixed(2)} suggests healthy but improvable efficiency.` });
  } else if (sharpe > 0.5) {
    insights.push({ type: 'warning', title: 'Weak Risk-Adjusted Returns', description: `Sharpe ratio ${sharpe.toFixed(2)} implies excess risk for the return.` });
  } else {
    insights.push({ type: 'negative', title: 'Poor Risk-Adjusted Returns', description: `Sharpe ratio ${sharpe.toFixed(2)} signals inefficiency in risk-taking.` });
  }

  /** ---------- Allocation Analysis ---------- **/
  const stockAllocation = allocations.Stocks || 0;
  const bondAllocation = allocations.Bonds || 0;
  const cashAllocation = allocations.Cash || 0;

  if (stockAllocation > 80) {
    recommendations.push({
      priority: 'medium',
      title: 'High Equity Exposure',
      description: `With ${stockAllocation}% in stocks, consider diversifying for better risk control.`,
    });
  }

  if (bondAllocation < 10 && stockAllocation > 70) {
    recommendations.push({
      priority: 'medium',
      title: 'Add Fixed Income Allocation',
      description: 'Introduce at least 10–20% bonds for stability.',
    });
  }

  if (cashAllocation > 20) {
    recommendations.push({
      priority: 'low',
      title: 'High Cash Allocation',
      description: `With ${cashAllocation}% in cash, you may miss growth opportunities.`,
    });
  }

  /** ---------- Scoring ---------- **/
  const performanceScore = calculatePerformanceScore(metrics);
  const riskScore = calculateRiskScore(metrics);
  const diversificationScore = calculateDiversificationScore(allocations);

  const overallScore = (
    performanceScore * 0.3 +
    (100 - riskScore) * 0.3 +
    diversificationScore * 0.2 +
    Math.min(100, metrics.sharpeRatio * 50) * 0.2
  ).toFixed(0);

  const summary =
    overallScore > 80 ? 'Excellent' :
    overallScore > 65 ? 'Good' :
    overallScore > 45 ? 'Fair' : 'Needs Improvement';

  return {
    overallRating,
    riskLevel,
    insights,
    recommendations: recommendations.length
      ? recommendations
      : [{ priority: 'low', title: 'Balanced Portfolio', description: 'Your allocation looks well-diversified. Keep monitoring annually.' }],
    metrics: { performanceScore, riskScore, diversificationScore, overallScore, summary },
  };
};

/** ---------- Monte Carlo Analysis ---------- **/
export const analyzeMonteCarloResults = (monteCarloResults, allocations, initialInvestment) => {
  if (!monteCarloResults || !monteCarloResults.statistics) return null;

  const { statistics, yearsToSimulate = 20 } = monteCarloResults;
  const insights = [];
  const recommendations = [];
  let confidenceLevel = 'Moderate';

  const medianGain = statistics.median - initialInvestment;
  const medianGainPercent = (medianGain / initialInvestment) * 100;

  if (medianGainPercent > 200) {
    insights.push({ type: 'positive', title: 'Exceptional Growth Potential', description: `Median outcome suggests a ${medianGainPercent.toFixed(0)}% gain.` });
    confidenceLevel = 'High';
  } else if (medianGainPercent > 100) {
    insights.push({ type: 'positive', title: 'Strong Growth Potential', description: `Median outcome suggests portfolio could double (${medianGainPercent.toFixed(0)}% gain).` });
  } else if (medianGainPercent > 50) {
    insights.push({ type: 'neutral', title: 'Moderate Growth Potential', description: `Expected gain of ${medianGainPercent.toFixed(0)}% over ${yearsToSimulate} years.` });
  } else if (medianGainPercent > 0) {
    insights.push({ type: 'neutral', title: 'Low Growth', description: `Median gain of ${medianGainPercent.toFixed(0)}% may not outpace inflation.` });
  } else {
    insights.push({ type: 'warning', title: 'Potential Loss', description: `Median loss of ${Math.abs(medianGainPercent).toFixed(0)}% suggests downside risk.` });
    confidenceLevel = 'Low';
  }

  if (statistics.probabilityOfLoss > 30) {
    insights.push({ type: 'warning', title: 'High Risk of Loss', description: `${statistics.probabilityOfLoss.toFixed(1)}% chance of loss.` });
    recommendations.push({ priority: 'high', title: 'Reduce Risk', description: 'Increase bonds or cash exposure to lower loss probability.' });
  }

  const p90Gain = ((statistics.p90 - initialInvestment) / initialInvestment) * 100;
  const p10Gain = ((statistics.p10 - initialInvestment) / initialInvestment) * 100;
  insights.push({
    type: 'info',
    title: 'Scenario Range',
    description: `Best 10%: +${p90Gain.toFixed(0)}%. Worst 10%: ${p10Gain >= 0 ? '+' : ''}${p10Gain.toFixed(0)}%.`,
  });

  return {
    confidenceLevel,
    insights,
    recommendations: recommendations.length
      ? recommendations
      : [{ priority: 'low', title: 'Stay the Course', description: 'Outlook positive; continue current plan.' }],
    scenarioAnalysis: {
      bestCase: { value: statistics.p90, probability: '10%' },
      medianCase: { value: statistics.median, probability: '50%' },
      worstCase: { value: statistics.p10, probability: '10%' },
    },
  };
};

/** ---------- Supporting Score Functions ---------- **/
function calculatePerformanceScore(metrics) {
  let score = 50;
  if (metrics.annualizedReturn > 15) score += 30;
  else if (metrics.annualizedReturn > 10) score += 25;
  else if (metrics.annualizedReturn > 7) score += 20;
  else if (metrics.annualizedReturn > 5) score += 15;
  else if (metrics.annualizedReturn > 0) score += 5;

  if (metrics.sharpeRatio > 2.0) score += 20;
  else if (metrics.sharpeRatio > 1.5) score += 15;
  else if (metrics.sharpeRatio > 1.0) score += 10;
  else if (metrics.sharpeRatio > 0.5) score += 5;

  return Math.min(100, Math.max(0, score));
}

function calculateRiskScore(metrics) {
  let score = 0;
  if (metrics.volatility > 25) score += 40;
  else if (metrics.volatility > 20) score += 30;
  else if (metrics.volatility > 15) score += 20;
  else if (metrics.volatility > 10) score += 10;
  else score += 5;

  if (metrics.maxDrawdown > 40) score += 40;
  else if (metrics.maxDrawdown > 30) score += 30;
  else if (metrics.maxDrawdown > 20) score += 20;
  else if (metrics.maxDrawdown > 10) score += 10;
  else score += 5;

  if (metrics.sharpeRatio > 1.5) score -= 15;
  else if (metrics.sharpeRatio > 1.0) score -= 10;

  return Math.min(100, Math.max(0, score));
}

function calculateDiversificationScore(allocations) {
  const values = Object.values(allocations);
  const meaningful = values.filter(v => v > 5).length;
  const concentration = values.reduce((sum, v) => sum + Math.pow(v / 100, 2), 0);
  const score = meaningful * 20 + (1 - concentration) * 20;
  return Math.min(100, Math.max(0, Math.round(score)));
}

export default { analyzeBacktestResults, analyzeMonteCarloResults };
